import { Suspense, lazy } from 'react';
import { Navigate, useRoutes, useLocation, Outlet } from 'react-router-dom';
// layouts
import MainLayout from '../layouts/main';
import DashboardLayout from '../layouts/dashboard';
import LogoOnlyLayout from '../layouts/LogoOnlyLayout';
import TransactionLayout from '../layouts/TransactionLayout';
import BlankLayout from '../layouts/BlankLayout';
// guards
import GuestGuard from '../guards/GuestGuard';
import AuthGuard from '../guards/AuthGuard';
// import RoleBasedGuard from '../guards/RoleBasedGuard';
// config
import { PATH_AFTER_LOGIN } from '../config';
// components
import LoadingScreen from '../components/LoadingScreen';

// ----------------------------------------------------------------------

const Loadable = (Component) => (props) =>
  (
    <Suspense fallback={<LoadingScreen loadable={true} />}>
      <Component {...props} />
    </Suspense>
  );

export default function Router() {
  return useRoutes([
    // Unified DashboardLayout Routes
    {
      path: '/',
      element: <DashboardLayout />,
      children: [
        // No Guard Routes
        { element: <Navigate to={PATH_AFTER_LOGIN} replace />, index: true },
        {
          path: '/',
          element: <HomeLayout />,
          children: [
            { path: 'home', index: true, element: <HomeBody /> },
            { path: 'promotion', index: true, element: <Promotion /> },
            { path: 'games/:categSlug', element: <GamesByCateg /> },
            { path: 'games/:categSlug/:companyId', element: <GamesByCateg /> },
          ],
        },
        { path: 'vip', element: <Vip /> },
        { path: 'promotion/view/:promotionId', element: <ViewPromotion /> },

        // GuestGuard Routes
        {
          path: 'auth',
          element: (
            <GuestGuard>
              <Outlet />
            </GuestGuard>
          ),
          children: [
            {
              path: 'login',
              children: [
                {
                  path: '',
                  element: <HomeLayout />,
                  children: [{ path: '', element: <HomeBody popupAuth={'1'} />, index: true }],
                },
              ],
            },
            {
              path: 'register',
              children: [
                {
                  path: '',
                  element: <Register />,
                  // children: [{ path: '', element: <Register />, index: true }],
                },
              ],
            },
            // { path: 'login-unprotected', element: <Login /> },
            // { path: 'register-unprotected', element: <Register /> },
            { path: 'reset-password/:token', element: <ResetPassword /> },
            { path: 'new-password', element: <NewPassword /> },
            { path: 'verify', element: <VerifyCode /> },
          ],
        },

        // AuthGuard Routes
        {
          path: '/',
          element: (
            <AuthGuard>
              <Outlet />
            </AuthGuard>
          ),
          children: [
            // History Routes
            {
              path: 'history',
              children: [
                { path: 'bet', element: <GameHistory /> },
                { path: 'transaction', element: <TransactionHistory /> },
              ],
            },

            // User Routes
            {
              path: 'user',
              children: [
                { path: 'account', element: <AccountProfile /> },
                { path: 'banks', element: <MemberBankList /> },
              ],
            },

            {
              path: 'user',
              element: <TransactionLayout />,
              children: [{ path: 'wallet', element: <Wallet /> }],
            },

            // Referral Route
            { path: 'referral', element: <Referral /> },
            { path: 'referral/refer-a-friend', element: <ReferAFriend /> },
            { path: 'agent', element: <Agent /> },
            { path: 'invite', element: <Invite /> },
            { path: 'spin', element: <SpinTheWheel /> },
          ],
        },
      ],
    },

    {
      path: 'payStatus',
      element: <LogoOnlyLayout />,
      children: [
        {
          path: ':paymentMethod',
          element: <PaymentStatus />,
          index: true,
        },
      ],
    },

    // AngPau
    // {
    //   path: '/angpau',
    //   element: (
    //     // <AuthGuard>
    //     <AngPau />
    //     // </AuthGuard>ß
    //   ),
    // },

    // CustomPage Route
    {
      path: 'page',
      element: (
        // <AuthGuard>
        <DashboardLayout />
        // </AuthGuard>
      ),
      children: [
        { element: <Navigate to={PATH_AFTER_LOGIN} replace />, index: true },
        { path: ':customPage', element: <CustomPage /> },
      ],
    },

    // Download Page
    {
      path: '',
      element: <BlankLayout />,
      children: [{ path: 'download', element: <DownloadPage /> }],
    },

    // Main Routes
    {
      path: '*',
      element: <LogoOnlyLayout />,
      children: [
        { path: 'coming-soon', element: <ComingSoon /> },
        { path: 'maintenance', element: <Maintenance /> },
        { path: '500', element: <Page500 /> },
        { path: '404', element: <Page404 /> },
        { path: '403', element: <Page403 /> },
        { path: '*', element: <Navigate to="/404" replace /> },
      ],
    },
    // {
    //   path: '/',
    //   element: <MainLayout />,
    //   children: [
    //     { element: <HomePage />, index: true },
    //     { path: 'about-us', element: <About /> },
    //     { path: 'contact-us', element: <Contact /> },
    //     { path: 'faqs', element: <Faqs /> },
    //   ],
    // },
    { path: '*', element: <Navigate to="/404" replace /> },
  ]);
}

// AUTHENTICATION
// const Login = Loadable(lazy(() => import('../pages/auth/Login')));
const Register = Loadable(lazy(() => import('../views/auth/Register')));
const ResetPassword = Loadable(lazy(() => import('../pages/auth/ResetPassword')));
const NewPassword = Loadable(lazy(() => import('../pages/auth/NewPassword')));
const VerifyCode = Loadable(lazy(() => import('../pages/auth/VerifyCode')));

// HOME
// const Home = Loadable(lazy(() => import('../views/game/Home')));
const HomeLayout = Loadable(lazy(() => import('../views/game/HomeLayout')));
const HomeBody = Loadable(lazy(() => import('../views/game/HomeBody')));
const ProviderByCateg = Loadable(lazy(() => import('../views/game/ProviderByCateg')));

// GAMES
const GamesByCateg = Loadable(lazy(() => import('../views/game/GamesByCateg')));

// GAME HISTORY
const GameHistory = Loadable(lazy(() => import('../views/history/GameHistory')));

// TRANSACTION HISTORY
const TransactionHistory = Loadable(lazy(() => import('../views/history/TransactionHistory')));

// USER
const AccountProfile = Loadable(lazy(() => import('../views/user/AccountProfile')));
const MemberBankList = Loadable(lazy(() => import('../views/user/MemberBankList')));
const Wallet = Loadable(lazy(() => import('../views/user/Wallet')));

// INVITE
const Invite = Loadable(lazy(() => import('../views/share/Invite')));

// PROMOTION
const Promotion = Loadable(lazy(() => import('../views/promotion/Promotion')));
const ViewPromotion = Loadable(lazy(() => import('../views/promotion/ViewPromotion')));
const SpinTheWheel = Loadable(lazy(() => import('../views/promotion/SpinTheWheel')));
// const AngPau = Loadable(lazy(() => import('../views/promotion/AngPau')));

// PAYMENT STATS
const PaymentStatus = Loadable(lazy(() => import('../pages/PaymentStatus')));

// REFERRAL
const Referral = Loadable(lazy(() => import('../views/referral/Referral')));
const ReferAFriend = Loadable(lazy(() => import('../views/referral/ReferAFriend')));

// Bonus
const Vip = Loadable(lazy(() => import('../views/referral/Vip')));
const Agent = Loadable(lazy(() => import('../views/referral/Agent')));

// CUSTOM PAGE
const CustomPage = Loadable(lazy(() => import('../views/CustomPages')));

// DOWNLOAD PAGE
const DownloadPage = Loadable(lazy(() => import('../views/DownloadPage')));

// MAIN
const ComingSoon = Loadable(lazy(() => import('../pages/ComingSoon')));
const Maintenance = Loadable(lazy(() => import('../pages/Maintenance')));
const Page500 = Loadable(lazy(() => import('../pages/Page500')));
const Page403 = Loadable(lazy(() => import('../pages/Page403')));
const Page404 = Loadable(lazy(() => import('../pages/Page404')));
